import { useReducer, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import { ActivityLogFilterType } from '../types/filterOptionsType';
import {
  activities,
  activityTypes,
  initialState,
  sourceList,
  tabActivityOptions,
} from './activityFilterConstants';
import { MultiSelectInputType } from 'src/ui/components';
import { getStartDate } from './common';

export function filterReducer(
  state: ActivityLogFilterType,
  action: {
    type: keyof ActivityLogFilterType | 'reset' | 'preserve';
    value?: any;
    payload?: any;
    newState?: ActivityLogFilterType | undefined;
  },
) {
  switch (action.type) {
    case 'reset':
      return {
        ...action.value,
      };

    default:
      return {
        ...state,
        [action.type]: action.value,
      };
  }
}

export function useActivityLogFilterReducer(
  selectedToggleValue: string,
  isExternalUser: boolean,
): {
  filterOptions: ActivityLogFilterType;
  filterCount: number;
  setValues: (type: keyof ActivityLogFilterType, value: string) => void;
  resetFilters: () => void;
  setCategory: (value: Array<MultiSelectInputType>) => void;
  setCategoryActivity: (value: Array<MultiSelectInputType>) => void;
  setTabActivity: (value: Array<MultiSelectInputType>) => void;
  setSource: (value: Array<MultiSelectInputType>) => void;
} {
  const [filterOptions, setFilterOptions] = useReducer(
    filterReducer,
    {
      ...JSON.parse(JSON.stringify(initialState)),
      type: [...activityTypes],
      createdOn: !isExternalUser ? getStartDate() : '',
      source: [...sourceList],
    },
    (initial: ActivityLogFilterType) => {
      // const savedActivityLogFilterString =
      //   window.localStorage.getItem('ActivityLogFilter');
      const key = `ActivityLogFilter_${selectedToggleValue}`;
      const savedActivityLogFilterString = window.localStorage.getItem(key);
      if (savedActivityLogFilterString) {
        const activityLogFilter: ActivityLogFilterType = JSON.parse(
          savedActivityLogFilterString,
        );
        return {
          ...activityLogFilter,
          type: Array.isArray(activityLogFilter.type)
            ? [...activityLogFilter.type]
            : [...activityTypes],
          createdOn: !isExternalUser
            ? activityLogFilter.createdOn
              ? activityLogFilter.createdOn
              : getStartDate()
            : activityLogFilter.createdOn,
          source: [...(activityLogFilter?.source ?? [...sourceList])],
          tabActivity: Array.isArray(activityLogFilter.tabActivity)
            ? [...activityLogFilter.tabActivity]
            : [...(tabActivityOptions[selectedToggleValue] ?? [])],
          categoryActivity: Array.isArray(activityLogFilter.categoryActivity)
            ? [...activityLogFilter.categoryActivity]
            : [...activities],
        };
      }
      return {
        ...initial,
        tabActivity: [...(tabActivityOptions[selectedToggleValue] ?? [])],
        categoryActivity: [...activities],
      };
    },
  );
  //
  useEffect(() => {
    const key = `ActivityLogFilter_${selectedToggleValue}`;
    const savedActivityLogFilterString = window.localStorage.getItem(key);

    if (savedActivityLogFilterString) {
      const activityLogFilter: ActivityLogFilterType = JSON.parse(
        savedActivityLogFilterString,
      );
      setFilterOptions({
        type: 'reset',
        value: {
          ...activityLogFilter,
          type: Array.isArray(activityLogFilter.type)
            ? [...activityLogFilter.type]
            : [...activityTypes],
          createdOn: !isExternalUser
            ? activityLogFilter.createdOn
              ? activityLogFilter.createdOn
              : getStartDate()
            : activityLogFilter.createdOn,
          source: [...(activityLogFilter?.source ?? [...sourceList])],
          tabActivity: Array.isArray(activityLogFilter.tabActivity)
            ? [...activityLogFilter.tabActivity]
            : [...(tabActivityOptions[selectedToggleValue] ?? [])],
          categoryActivity: Array.isArray(activityLogFilter.categoryActivity)
            ? [...activityLogFilter.categoryActivity]
            : [...activities],
        },
      });
    } else {
      setFilterOptions({
        type: 'reset',
        value: {
          ...JSON.parse(JSON.stringify(initialState)),
          type: [...activityTypes],
          createdOn: !isExternalUser ? getStartDate() : '',
          source: [...sourceList],
          tabActivity: [...(tabActivityOptions[selectedToggleValue] ?? [])],
          categoryActivity: [...activities],
        },
      });
    }
  }, [isExternalUser, selectedToggleValue]); // runs every time the tab changes

  //for filtercount
  const filterCount = useMemo(() => {
    let count: number = 0;
    const obj1: any = { ...initialState };
    // null = "all selected" (the default); [] or [items] = changed from default
    obj1.type = null;
    obj1.categoryActivity = null;
    if (tabActivityOptions[selectedToggleValue]) {
      obj1.tabActivity = null;
    } else {
      delete obj1['tabActivity'];
    }
    const obj2: any = {
      ...JSON.parse(JSON.stringify(filterOptions)),
      source:
        !filterOptions?.source ||
        filterOptions?.source.length === 0 ||
        (filterOptions?.source.length > 0 &&
          filterOptions?.source.find(
            (item: MultiSelectInputType) => item.value === 0,
          ))
          ? undefined
          : filterOptions?.source,
      tabActivity: filterOptions?.tabActivity?.some(
        (item: MultiSelectInputType) => item.value === 0,
      )
        ? null
        : (filterOptions?.tabActivity ?? []),
      categoryActivity: filterOptions?.categoryActivity?.some(
        (item: MultiSelectInputType) => item.value === 0,
      )
        ? null
        : (filterOptions?.categoryActivity ?? []),
      type: filterOptions?.type?.some(
        (item: MultiSelectInputType) => item.value === 0,
      )
        ? null
        : (filterOptions?.type ?? []),
    };

    delete obj1['keywordSearch'];
    delete obj1['sortOrder'];
    delete obj1['sortType'];

    if (selectedToggleValue === 'personRepository') {
      delete obj1['submissionNumber'];
      delete obj1['personAlertId'];
      delete obj1['type'];
    }
    if (selectedToggleValue === 'personAlerts') {
      delete obj1['submissionNumber'];
      delete obj1['personId'];
      delete obj1['type'];
    }
    if (selectedToggleValue === 'callDocuments') {
      delete obj1['submissionNumber'];
      delete obj1['personAlertId']; //alert number
      delete obj1['personId'];
      delete obj1['type'];
    }
    if (selectedToggleValue === 'hospitalForm') {
      delete obj1['personAlertId'];
      delete obj1['personId'];
      delete obj1['type'];
    }

    if (selectedToggleValue === 'all') {
      delete obj1['submissionNumber'];
      delete obj1['personAlertId'];
      delete obj1['personId'];
      delete obj1['tabActivity'];
    }

    if (selectedToggleValue !== 'all') {
      delete obj1['categoryActivity'];
      delete obj1['type'];
    }

    if (isExternalUser) {
      delete obj1['accountCidPid'];
      delete obj1['docId'];
      delete obj1['type'];
      delete obj1['categoryActivity'];
      delete obj1['personAlertId'];
      delete obj1['personId'];
      delete obj1['userId'];
      delete obj1['personId'];
      delete obj1['submissionNumber'];
    }

    Object.keys(obj1).forEach(key => {
      if (JSON.stringify(obj1[key]) !== JSON.stringify(obj2[key])) {
        count++;
      }
    });
    return count;
  }, [filterOptions, isExternalUser, selectedToggleValue]);

  function setValues(type: keyof ActivityLogFilterType, value: string) {
    setFilterOptions({
      type,
      value,
    });
  }

  function setCategory(
    value: Array<{ value: number | string; label: string | undefined }>,
  ) {
    setFilterOptions({
      type: 'type',
      value,
    });
  }

  function setCategoryActivity(
    value: Array<{ value: number | string; label: string | undefined }>,
  ) {
    setFilterOptions({
      type: 'categoryActivity',
      value,
    });
  }

  function setTabActivity(
    value: Array<{ value: number | string; label: string | undefined }>,
  ) {
    setFilterOptions({
      type: 'tabActivity',
      value,
    });
  }

  function setSource(
    value: Array<{ value: number | string; label: string | undefined }>,
  ) {
    setFilterOptions({
      type: 'source',
      value,
    });
  }

  useEffect(() => {
    const key = `ActivityLogFilter_${selectedToggleValue}`;
    window.localStorage.setItem(key, JSON.stringify(filterOptions));
  }, [filterOptions, selectedToggleValue]);

  function resetFilters() {
    setFilterOptions({
      type: 'reset',
      value: {
        ...JSON.parse(JSON.stringify(initialState)),
        type: [...activityTypes],
        createdOn: !isExternalUser ? getStartDate() : '',
        source: [...sourceList],
        tabActivity: [...(tabActivityOptions[selectedToggleValue] ?? [])],
        categoryActivity: [...activities],
      },
    });
  }

  return {
    filterOptions,
    filterCount,
    setValues,
    resetFilters,
    setCategory,
    setCategoryActivity,
    setTabActivity,
    setSource,
  };
}
